/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { FormControlLabel, Checkbox } from "@mui/material";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import ArgosMenuItem from "../../../../../../components/Form/Select/ArgosMenuItem";
import ArgosSelect from "../../../../../../components/Form/Select/ArgosSelect";
import { useAppSelector } from "../../../../../../../redux/hooks";
import { selectGlobalDialogState } from "../../../../../../../redux/ducks/GlobalDialog/slice";
import { MRT_Cell, MRT_ColumnDef, MaterialReactTable } from "material-react-table";
import {useEffect, useRef, useState} from "react";
import { CamerarPropertiesObject, CamerarPropertiesTableRow, cameraPropsKeyToTitleMap } from "../../constants";
import { useHasWriteAccess } from "../../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import { toast } from "react-hot-toast";
import {roundToXDecimals} from "../../../../../../../helpers/helpers";
import {adjustColumnSizes} from "../../../../../../../helpers/tableHelpers";
import {ArgosTableSimple} from "../../../../../../components/Table/ArgosTableSimple";

interface IImagerProps{
  baselineData: any
  sensorCameraData: any
  handleResolutionChange: any
  setHorizontalPixelsAR: any
  setVerticalPixelsAR: any
  setLockAR: any
  lockAR: any
}

const ImagerPeroperties = (props: IImagerProps) => {

  const isDialogOpen = useAppSelector(selectGlobalDialogState).isOpen
  const [tableData, setTableData] = useState<CamerarPropertiesTableRow[]>([]);
  const hasWriteAccess = useHasWriteAccess();
  const [validationErrors, setValidationErrors] = useState<any>({});

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState<number>(0);

  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.offsetWidth);
    }
  }, []);

  const imagerData = props.sensorCameraData.plotData.imagerTable.concat([
      { "label": "Custom",
       "width": '',
       "height": ''}
     ]);
  useEffect(() => {
    mapPropsToRowFormat()
  }, [props])
  const mapPropsToRowFormat = () => {
    let _tableData: CamerarPropertiesTableRow[] = []
    let sensorCameraData = props?.sensorCameraData;
    let baselineData = props?.baselineData;
    if (!sensorCameraData || !baselineData) {
      return;
    }
    for (const key in sensorCameraData){
      if(key.includes('imager')){
        baselineData[key] = sensorCameraData[key];
      }
    }
    cameraPropsKeyToTitleMap.forEach((element: CamerarPropertiesObject) => {
      let propertyValue = baselineData[element.key]
      // round property value to 3 decimal places
      if (typeof propertyValue !== 'string'){
        propertyValue = roundToXDecimals(propertyValue, 3)
      }
      let camerapropertyObjectAsRow: CamerarPropertiesTableRow = {
        propertyName: element.label,
        propertyValue,
        unit: element.unit
      }
      _tableData.push(camerapropertyObjectAsRow)
    })
    setTableData(_tableData)    
  }
  function helperSetValidationErrors(newVal: string, cell: any, message: string) {
    setValidationErrors({
        ...validationErrors,
        [cell.id]: message
    })

    toast.error(message,
        {
            style: {
                marginTop: "300px"
            },
            duration: 3000
        })
  }

  const validateInput = (newVal: string, cell: any) => {
    let newValnum = Number(newVal)
    if (isNaN(newValnum)) {
        helperSetValidationErrors(newVal, cell, `Not valid value, must be a number`)
        return false
    }
    return true
  }
  const getCommonEditTextFieldProps =
  (
      cell: MRT_Cell<CamerarPropertiesTableRow>,
  ): MRT_ColumnDef<CamerarPropertiesTableRow>['muiEditTextFieldProps'] => {
      return {
          error: !!validationErrors[cell.id],
          helperText: validationErrors[cell.id],
          onKeyDown: (e) => {
              if (e.code == "Tab") {
                  e.stopPropagation()
                  e.preventDefault()
              }
          },
          onBlur: (event) => {
            if(cell.row.original.propertyName == "Horizontal Pixels" || cell.row.original.propertyName == "Vertical Pixels"){
              const isValid = validateInput(event.target.value, cell);
              if (isValid) {
                delete validationErrors[cell.id];
                if(cell.row.original.propertyName == "Horizontal Pixels"){
                  props?.setHorizontalPixelsAR(event.target.value)
                }
                else if  (cell.row.original.propertyName == "Vertical Pixels"){
                  props?.setVerticalPixelsAR(event.target.value)
                }
              }
            }
          },
      };
  }
  let cameraPropertiesTableColumns: MRT_ColumnDef<CamerarPropertiesTableRow>[] = [
    {
      header: "Property",
      accessorKey: "propertyName",
      enableEditing: false,
    },
    {
      header: "Value",
      accessorKey: "propertyValue",
      enableEditing: true,
      size: 500,
      muiEditTextFieldProps: ({ cell }) => ({
        ...getCommonEditTextFieldProps(cell),
        disabled: !hasWriteAccess || 
        props?.baselineData.resolution != 'Custom' || 
        (props?.baselineData.resolution == 'Custom' && (cell.row.original.propertyName == "Aspect Ratio") || cell.row.original.propertyName == "Resolution (MP)") 
      }),
      Cell: ({ cell }) => {
        if(cell.row.original.propertyName == "Resolution"){
          return (
            <ArgosSelect
            value={props?.baselineData?.resolution}
            onChange={props?.handleResolutionChange}
          >
            {imagerData.map((imagerElement: any, i: number) => <ArgosMenuItem key={i} value={imagerElement.label}>{imagerElement.label}</ArgosMenuItem>)}
    
          </ArgosSelect>)
        }
        else{
          return <div>{cell.getValue()  as any}</div>
        }
      }
    },
    {
      header: "Unit",
      accessorKey: "unit",
      enableEditing: false,
    },
  ]

  return (
  <ArgosFlexbox vertical style={{width: "100%"}}>
     <div style={{padding: "5px", width: "100%"}} ref={tableRef}>
        <ArgosTableSimple 
          columns={cameraPropertiesTableColumns}
          data={tableData}

          initialState={{density: 'compact'}}
          editDisplayMode="cell"
          enableEditing
          enableBottomToolbar={false}
        />
    </div>   
   
      <div>
        <FormControlLabel
        style={{padding: "0px 0px 0px 10px"}}
          control={
            <Checkbox
              onChange={(e) => {
                props?.setLockAR(e.target.checked);
              }}
              checked={props?.lockAR}
              disabled={props?.baselineData?.resolution != 'Custom'}
            />
          }
          label="Lock Aspect Ratio"
        />
      </div>
   </ArgosFlexbox>)
}
export default ImagerPeroperties;



